import { memo, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { cookie } from '../../utils/cookie';
import { socialLinks } from '../../utils/constants';
import logo from 'src/assets/images/solfin-coloured-logo.svg';
import WhatsAppToggleWidget from './WhatsAppToggleWidget';
import { useMediaQuery, useTheme } from '@mui/material';
import RaiseQuery from 'src/CustomerDashboard/components/RaiseAQurey/RaiseQurey';

const contactInfo = [
  { icon: 'fa-user', text: 'XSFinance and Investment Pvt. Ltd.' },
  { icon: 'fa-address-card', text: 'U65999HR2022PTC108105' },
  { icon: 'fa-map-marker', text: 'Sector-62, Gurgaon - 122001' },
  {
    icon: 'fa-envelope',
    text: (
      <a
        href="mailto:support@solfin.co.in"
        className="text-white text-decoration-none"
      >
        support@solfin.co.in
      </a>
    )
  },
  {
    icon: 'fa-phone',
    text: (
      <a href="tel:+919599446877" className="text-white text-decoration-none">
        +91 9599446877
      </a>
    )
  }
];

const Icon = ({ href = '', iconName = '' }) => (
  <a
    className="rounded-pill text-decoration-none d-flex align-items-center justify-content-center text-theme-green"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    <i className={`fa fa-${iconName}`}></i>
  </a>
);

const LinkList = ({ links }) => (
  <ul className="list-unstyled m-0 p-0">
    {links.map(({ to, text, onClick, external }, index) => (
      <li key={index}>
        {external ? (
          <a href={to} onClick={onClick || null}>
            {text}
          </a>
        ) : (
          <NavLink to={to} onClick={onClick || null}>
            {text}
          </NavLink>
        )}
      </li>
    ))}
  </ul>
);

const ContactInfo = ({ info }) => (
  <div className="contact-via text-white">
    {info.map(({ icon, text }, index) => (
      <p className="d-flex" key={index}>
        <span>
          <i className={`fa ${icon}`}></i>
        </span>
        <span>{text}</span>
      </p>
    ))}
  </div>
);

const FooterSection = ({ title, children }) => (
  <div className="col-xl-3 col-lg-3 col-sm-6 mt-lg-0 mt-4">
    <div className="footer-heading mt-4 text-white">
      <h6 className="text-white fw-bold">{title}</h6>
    </div>
    <div className="footer-link mt-lg-4 mt-3">{children}</div>
  </div>
);

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const deletePreference = () => {
    // Use the global reset function from CookieConsentBanner
    if (window.resetCookieConsent) {
      window.resetCookieConsent();
    } else {
      // Fallback if CookieConsentBanner not loaded - use eraseAll to remove from all domains
      cookie.eraseAll('solfin-cookie-consent');
      cookie.eraseAll('cookieConsent');
      console.log('🗑️  Cookie preferences reset (fallback mode)');
    }
  };

  const quickLinks = useMemo(
    () => [
      { to: '/solar-calculator', text: 'Solar Calculator' },
      { to: '/v2/about-us', text: 'About Us', external: true },
      { to: '/v2/contact', text: 'Contact Us', external: true },
      { to: '/v2/policies/xs-finance-privacy-policy', text: 'XS Finance Privacy Policy', external: true },
      { to: '/v2/policies/privacy-policy', text: 'Solfin Privacy Policy', external: true },
      { to: '/v2/policies/cookie-policy', text: 'Cookie Policy', external: true },
      { to: '#', text: 'Cookie Settings', onClick: deletePreference },
      { to: '/v2/ad-campaigns', text: 'Our Campaigns', external: true },
      { to: '/v2/policies/refund-and-cancellation-policy', text: 'Refund Policy', external: true },
      { to: '/v2/policies/fair-practice-code', text: 'Fair Practice Code', external: true },
      { to: '/v2/policies/KYC-and-AML-policy', text: 'KYC and AML Policy', external: true },
      { to: '/v2/policies/loan-policy', text: 'Loan Policy', external: true },
      { to: '/v2/policies/overdue-sma-and-npa', text: 'Overdue, SMA & NPA', external: true },
      {
        to: '/v2/policies/grievance-redressal-policy',
        text: 'Grievance Redressal Policy',
        external: true
      },
      { to: '/v2/policies/data-protection-rights', text: 'Data Protection Rights', external: true },
      { to: '/v2/policies/terms-and-conditions', text: 'Terms and Conditions', external: true },
      { to: '/v2/knowledge', text: 'Knowledge', external: true },
      { to: `${process.env.REACT_APP_INTERNAL_URL}/dashboard/login`, text: 'Internal Agent Login' },
      { to: '/v2/sitemap', text: 'Sitemap', external: true }
    ],
    []
  );

  return (
    <footer id="footer">
      <div className="main-footer">
        <div className="container custom-container">
          <div className="row">
            <FooterSection title="About Solfin">
              <div className="footer-about-solfin">
                <div className="footer-logo">
                  <img src={logo} alt="Solfin" title="Solfin" />
                </div>
                <div className="footer-text text-white mt-3 mt-xl-4">
                  <p>
                    Enabling finance for all sustainable, renewable and climate
                    friendly projects throughout India.
                  </p>
                </div>
              </div>
            </FooterSection>

            {/* Quick Links */}
            <FooterSection title="Quick Links">
              <LinkList links={quickLinks} />
            </FooterSection>
            {/* Contact Info */}
            <FooterSection title="Contact">
              <ContactInfo info={contactInfo} />
            </FooterSection>

            {/* Social Media */}
            <FooterSection title="Follow Us">
              <div className="solfin-social d-flex align-items-center">
                <div className="social-links d-flex">
                  {Object.keys(socialLinks).map((item, index) => (
                    <Icon
                      key={`${item}_${index}`}
                      href={socialLinks[item]}
                      iconName={item}
                    />
                  ))}
                </div>
              </div>
            </FooterSection>
          </div>
        </div>
      </div>
      <div className="solfin-copyright">
        <div className="container custom-container">
          <div className="row">
            <div className="col-md-12 text-center">
              <span>
                Reserve Bank of India (RBI) - Certificate of Registration:
                N-13.02269
              </span>
            </div>
            <div className="col-md-12 text-center">
              <span>
                Copyright &copy; <strong>Solfin</strong>{' '}
                {new Date().getFullYear()}. All Rights Reserved.
              </span>
            </div>
            <div className="col-md-12 text-center">
              <span>
                Solfin Sustainable Finance Private Limited (formerly known as
                Dhanvikas Fiscal Services Private Limited)
              </span>
            </div>
          </div>
        </div>
      </div>
      {isMobile ? <WhatsAppToggleWidget /> : <RaiseQuery />}
    </footer>
  );
};

export default memo(Footer);
